'use client';

import React, { useEffect, useId, useRef, useState } from 'react';
import { applyReadableNodeLabelColorsToElement } from './readable-node-label-colors';
import { isDevelopment } from '@/lib/env';
type MermaidRendererProps = {
  content: string | undefined;
  className?: string;
  onError?: () => void;
  hideOnError?: boolean;
};

// Internal implementation of the renderer
function MermaidRendererImpl({
  content,
  className,
  onError,
  hideOnError
}: MermaidRendererProps) {
  const [svg, setSvg] = useState<string>('');
  const [error, setError] = useState<string | null>(null);
  const svgContainerRef = useRef<HTMLDivElement>(null);

  // Use React's built-in useId hook to generate a stable, unique ID
  const uniqueId = useId();
  const diagramId = `mermaid-diagram-${uniqueId.replace(/:/g, '')}`;
  useEffect(() => {
    let isMounted = true;
    let timer: ReturnType<typeof setTimeout> | undefined;
    // Dynamically import mermaid only on the client-side
    import('mermaid').then(mermaid => {
      if (!isMounted) {
        return;
      }

      // Initialize Mermaid when the component mounts or content changes
      mermaid.default.initialize({
        theme: 'dark',
        securityLevel: 'strict',
        startOnLoad: false,
        logLevel: 'error',
        // Disable error rendering
        suppressErrorRendering: true
      });
      const renderDiagram = async () => {
        if (!content) {
          if (isMounted) {
            setError('No diagram content provided');
            onError?.();
          }
          return;
        }
        try {
          // Use the unique ID for rendering
          const {
            svg: svg_0
          } = await mermaid.default.render(diagramId, content);
          if (isMounted) {
            setSvg(svg_0);
            setError(null);
          }
        } catch (err) {
          console.error('Mermaid rendering error:', err);
          // We still track the error internally, but don't show it
          if (isMounted) {
            setError('Failed to render diagram');
            onError?.();
          }
        }
      };

      // Small delay to ensure the component is fully mounted
      timer = setTimeout(() => {
        renderDiagram();
      }, 0);
    }).catch(err_0 => {
      console.error('Failed to load mermaid library:', err_0);
      if (isMounted) {
        setError('Failed to load diagram library');
        onError?.();
      }
    });
    return () => {
      isMounted = false;
      if (timer) {
        clearTimeout(timer);
      }
    };
  }, [content, diagramId, onError]);
  useEffect(() => {
    if (svg && svgContainerRef.current) {
      applyReadableNodeLabelColorsToElement(svgContainerRef.current);
    }
  }, [svg]);

  // Return an empty container if there's an error
  if (error) {
    // If hideOnError is true, return null to completely hide the component
    if (hideOnError) {
      return null;
    }
    return <div className={`mx-auto flex max-w-full flex-col items-center justify-center rounded-md border border-border bg-muted/20 p-4 text-[12px] italic text-muted-foreground ${className || ''}`} title={error}>
        <div>Mermaid diagram failed to render.</div>
        {isDevelopment() && content && <pre className="mt-2 max-h-60 overflow-auto whitespace-pre-wrap text-left text-xs opacity-75">
            {content}
          </pre>}
      </div>;
  }

  // Show loading state while diagram is being rendered
  if (!svg && content) {
    return <div className={`flex items-center justify-center rounded-md border border-border bg-muted/20 min-h-[200px] p-4 ${className || ''}`}>
        <div className="animate-pulse text-sm text-muted-foreground">
          Generating architecture diagram...
        </div>
      </div>;
  }
  return <div ref={svgContainerRef} className={`flex items-center justify-center rounded-md border border-border p-4 ${className || ''}`} dangerouslySetInnerHTML={{
    __html: svg
  }} />;
}

// Export a memoized version to prevent unnecessary re-renders
export const MermaidRenderer = React.memo(MermaidRendererImpl);